import { useState, useEffect } from "react";
import { Delete, Add, InfoOutlined, ExpandMore, ExpandLess } from "@mui/icons-material";
import RiskScoreSlider from "./RiskScoreSlider";
import { customOptionPrimaryLabel, customOptionSecondaryText } from "./customOptionHelpers";

// Question types with display labels (aligned with TempQuestion enum)
const QUESTION_TYPES = [
    { value: 'D/ND', label: 'Disclosure / No Disclosure' },
    { value: '1to5', label: '1 to 5' },
    { value: 'Date', label: 'Date Picker' },
    { value: 'YN/F/T', label: 'Yes | No | N/A' },
    { value: 'YN/TA', label: 'Yes | No | N/A + Text Area' },
    { value: 'YN/IMG', label: 'Yes | No | N/A + Image' },
    { value: 'FILE', label: 'File Upload Only' },
    { value: 'TA', label: 'Only Textarea' },
    { value: 'DropDown', label: 'DropDown' },
    { value: 'Multi', label: 'Multi' },
];

// Legacy DB values mapped to a select option value (YN stored as yesNo in UI)
const QUESTION_TYPE_SELECT_ALIASES = { YN: 'yesNo' };

const normalizeQuestionTypeForSelect = (questionType) => {
    const raw = String(questionType || '').trim();
    if (!raw || raw === 'null') return '';
    return QUESTION_TYPE_SELECT_ALIASES[raw] || raw;
};

const getQuestionTypeSelectOptions = (questionType) => {
    const normalized = normalizeQuestionTypeForSelect(questionType);
    if (normalized && !QUESTION_TYPES.some((t) => t.value === normalized)) {
        return [...QUESTION_TYPES, { value: normalized, label: normalized }];
    }
    return QUESTION_TYPES;
};

const getQuestionTypeLabel = (questionType) => {
    const normalized = normalizeQuestionTypeForSelect(questionType);
    return QUESTION_TYPES.find((t) => t.value === normalized)?.label || questionType || '';
};

// Predefined options for specific question types
const PREDEFINED_OPTIONS = {
    'yesNo': ['Yes', 'No', 'N/A'],
    'YN/F/T': ['Yes', 'No', 'N/A'],
    'D/ND': ['Disclosure', 'No Disclosure'],
    '1to5': ['1', '2', '3', '4', '5']
};

const QuestionForm = ({
    question,
    index,
    moduleKey,
    sectionIndex,
    subfolderPath = [],
    isExpanded = false,
    onToggleExpand,
    onUpdate,
    onRemove,
    errors = {},
    hideBorder = false,
    hideExpand = false,
    hideDelete = false
}) => {
    const [customOptions, setCustomOptions] = useState(question.customOptions || [{ value: '', info: '' }]);
    const [predefinedOptionsInfo, setPredefinedOptionsInfo] = useState(question.predefinedOptionsInfo || {});
    const [dateInfo, setDateInfo] = useState(question.dateInfo || '');
    const [tooltip, setTooltip] = useState(question.tooltip || '');
    const [toolboxOffer, setToolboxOffer] = useState(question.toolboxOffer || '');
    const [riskDescription, setRiskDescription] = useState(question.riskDescription || '');
    const [recommendDescription, setRecommendDescription] = useState(question.recommendDescription || '');
    const [riskScores, setRiskScores] = useState(question.riskScores || {});

    const generateQuestionNumber = () => {
        // Prefer stored question number if available so display always matches persisted qNo
        if (question.qNo) {
            return question.qNo;
        }

        if (!moduleKey) return `Q${question.questionNumber || index + 1}`;

        // Format section number as 3 digits (001, 002, etc.)
        const formattedSectionNumber = String(sectionIndex + 1).padStart(3, '0');
        const questionNum = question.questionNumber || index + 1;

        // Build the question number with subfolder path
        let qNo = `${moduleKey}${formattedSectionNumber}`;

        // Add subfolder path if exists (e.g., .1.2 for nested subfolders)
        if (subfolderPath && subfolderPath.length > 0) {
            qNo += '.' + subfolderPath.join('.');
        }

        // Add question number
        qNo += `.${questionNum}`;

        return qNo;
    };

    const effectiveQuestionType = normalizeQuestionTypeForSelect(question.questionType) || question.questionType;

    useEffect(() => {
        if (['DropDown', 'Radio', 'Multi'].includes(effectiveQuestionType)) {
            setCustomOptions(question.customOptions || [{ value: '', info: '' }]);
        }

        // Always update these fields when question changes
        setPredefinedOptionsInfo(question.predefinedOptionsInfo || {});
        setDateInfo(question.dateInfo || '');
        setTooltip(question.tooltip || '');
        setToolboxOffer(question.toolboxOffer || '');
        setRiskDescription(question.riskDescription || '');
        setRecommendDescription(question.recommendDescription || '');
        setRiskScores(question.riskScores || {});
    }, [question, effectiveQuestionType]);

    const handleQuestionChange = (field, value) => {
        const updatedQuestion = { ...question, [field]: value };

        // Reset options when question type changes
        if (field === 'questionType') {
            updatedQuestion.customOptions = [{ value: '', info: '' }];
            updatedQuestion.predefinedOptionsInfo = {};
            updatedQuestion.dateInfo = '';
            updatedQuestion.riskScores = {};
            setCustomOptions([{ value: '', info: '' }]);
            setPredefinedOptionsInfo({});
            setDateInfo('');
            setRiskScores({});
        }

        onUpdate(updatedQuestion);
    };

    const handleTooltipChange = (value) => {
        setTooltip(value);
        handleQuestionChange('tooltip', value);
    };

    const handleToolboxOfferChange = (value) => {
        setToolboxOffer(value);
        handleQuestionChange('toolboxOffer', value);
    };

    const handleRiskDescriptionChange = (value) => {
        setRiskDescription(value);
        handleQuestionChange('riskDescription', value);
    };

    const handleRecommendDescriptionChange = (value) => {
        setRecommendDescription(value);
        handleQuestionChange('recommendDescription', value);
    };

    const handleRiskScoreChange = (scores) => {
        setRiskScores(scores);
        handleQuestionChange('riskScores', scores);
    };

    const handleCustomOptionChange = (optionIndex, field, value) => {
        const newOptions = [...customOptions];
        newOptions[optionIndex][field] = value;
        setCustomOptions(newOptions);
        handleQuestionChange('customOptions', newOptions);
    };

    /** Sync value + name so APIs that store either shape stay consistent (e.g. investor-lead). */
    const handleCustomOptionPrimaryChange = (optionIndex, newLabel) => {
        const newOptions = [...customOptions];
        const prev = { ...(newOptions[optionIndex] || {}) };
        prev.value = newLabel;
        prev.name = newLabel;
        newOptions[optionIndex] = prev;
        setCustomOptions(newOptions);
        handleQuestionChange('customOptions', newOptions);
    };

    const handleCustomOptionSecondaryChange = (optionIndex, newText) => {
        const newOptions = [...customOptions];
        const prev = { ...(newOptions[optionIndex] || {}) };
        prev.info = newText;
        prev.description = newText;
        newOptions[optionIndex] = prev;
        setCustomOptions(newOptions);
        handleQuestionChange('customOptions', newOptions);
    };

    const addCustomOption = () => {
        const newOptions = [...customOptions, { value: '', info: '' }];
        setCustomOptions(newOptions);
        handleQuestionChange('customOptions', newOptions);
    };

    const removeCustomOption = (optionIndex) => {
        const newOptions = customOptions.filter((_, idx) => idx !== optionIndex);
        setCustomOptions(newOptions);
        handleQuestionChange('customOptions', newOptions);
    };

    const handlePredefinedOptionInfo = (optionValue, info) => {
        const newInfo = { ...predefinedOptionsInfo, [optionValue]: info };
        setPredefinedOptionsInfo(newInfo);
        handleQuestionChange('predefinedOptionsInfo', newInfo);
    };

    const handleDateInfoChange = (info) => {
        setDateInfo(info);
        handleQuestionChange('dateInfo', info);
    };

    // Check if question type has predefined options
    const hasPredefinedOptions = () => PREDEFINED_OPTIONS.hasOwnProperty(effectiveQuestionType);

    // Check if question type needs custom options
    const needsCustomOptions = () => ['DropDown', 'Radio', 'Multi'].includes(effectiveQuestionType);

    // Check if question type is Date
    const isDateType = () => effectiveQuestionType === 'Date';

    return (
        <div className="br10px mb1rem" style={{
            background: '#fafafa',
            border: hideBorder ? 'none' : '1px solid #e0e0e0',
            transition: 'all 0.3s ease',
            overflow: 'hidden',
            minWidth: 0,
            maxWidth: '100%'
        }}>
            {/* Question Header - Always Visible */}
            <div
                className="df jcsb aic"
                style={{
                    padding: '1rem 1.5rem',
                    background: isExpanded ? 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)' : 'white',
                    borderBottom: isExpanded ? '1px solid #e0e0e0' : 'none',
                    transition: 'all 0.3s ease'
                }}
            >
                <div className="df aic flex-1" style={{ gap: '12px' }}>
                    {/* Expand/Collapse Button */}
                    {!hideExpand && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleExpand();
                            }}
                            className="df aic jcc cp"
                            style={{
                                border: 'none',
                                background: isExpanded ? '#667eea' : '#e0e0e0',
                                color: 'white',
                                padding: '6px',
                                borderRadius: '6px',
                                transition: 'all 0.2s ease',
                                minWidth: '32px',
                                minHeight: '32px'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.background = isExpanded ? '#5568d3' : '#d0d0d0';
                                e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.background = isExpanded ? '#667eea' : '#e0e0e0';
                                e.currentTarget.style.transform = 'scale(1)';
                            }}
                        >
                            {isExpanded ?
                                <ExpandLess style={{ fontSize: '20px' }} /> :
                                <ExpandMore style={{ fontSize: '20px' }} />
                            }
                        </button>
                    )}

                    {/* Question Summary - Shows when collapsed */}
                    <div className="flex-1" style={{ minWidth: 0 }}>
                        {!isExpanded ? (
                            <div className="df aic" style={{ gap: '8px', flexWrap: 'wrap' }}>
                                <span
                                    className="fs0-85rem fw600"
                                    style={{
                                        color: '#667eea',
                                        fontFamily: 'monospace',
                                        background: '#667eea20',
                                        padding: '4px 10px',
                                        borderRadius: '6px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {generateQuestionNumber()}
                                </span>
                                <span style={{ color: '#999', margin: '0 4px' }}>–</span>
                                <span
                                    className="fs0-9rem"
                                    style={{
                                        color: '#333',
                                        fontWeight: '500',
                                        flex: 1,
                                        minWidth: 0,
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    {question.qDescription || 'New Question - Click to edit'}
                                </span>
                                {question.questionType && (
                                    <span
                                        className="fs0-75rem"
                                        style={{
                                            background: '#4CAF50',
                                            color: 'white',
                                            padding: '3px 8px',
                                            borderRadius: '4px',
                                            fontWeight: '500',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {getQuestionTypeLabel(question.questionType)}
                                    </span>
                                )}
                            </div>
                        ) : (
                            <h4 className="fs0-95rem fw600" style={{ color: '#667eea', margin: 0 }}>
                                Question {index + 1}
                            </h4>
                        )}
                    </div>

                    {/* Delete Button */}
                    {!hideDelete && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemove();
                            }}
                            className="df aic jcc cp"
                            style={{
                                border: 'none',
                                background: 'transparent',
                                color: '#dc3545',
                                padding: '6px',
                                borderRadius: '6px',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.background = '#fee';
                                e.currentTarget.style.color = '#c82333';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = '#dc3545';
                            }}
                            title="Remove Question"
                        >
                            <Delete style={{ fontSize: '18px' }} />
                        </button>
                    )}
                </div>
            </div>

            {/* Question Details - Show when expanded */}
            {isExpanded && (
                <div className="p1-5rem">
                    <div className="dg gg1rem module-form-grid" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}>
                        {/* Question Number - Auto-generated */}
                        <div className="df fdc">
                            <label className="fs0-875rem fw400 mb0-625rem" style={{ color: '#555' }}>
                                Question Number <span style={{ color: '#4CAF50' }}>(Auto-generated)</span>
                            </label>
                            <input
                                type="text"
                                value={generateQuestionNumber()}
                                readOnly
                                className="bss bw1px w100"
                                style={{
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd',
                                    fontSize: '0.95rem',
                                    outline: 'none',
                                    background: '#f5f5f5',
                                    color: '#333',
                                    cursor: 'not-allowed',
                                    fontFamily: 'monospace',
                                    fontWeight: '500'
                                }}
                                placeholder="Will be generated"
                            />
                            <small className="fs11px mt0-375rem" style={{ color: '#999' }}>
                                {subfolderPath && subfolderPath.length > 0
                                    ? `Format: MODULEKEY + SECTION# + SUBFOLDER# + QUESTION# (e.g., UMS001.1.1)`
                                    : `Format: MODULEKEY + SECTION# + QUESTION# (e.g., UMS001.1)`
                                }
                            </small>
                        </div>

                        {/* Question Type */}
                        <div className="df fdc">
                            <label className="fs0-875rem fw400 mb0-625rem" style={{ color: '#555' }}>
                                Question Type <span style={{ color: '#dc3545' }}>*</span>
                            </label>
                            <select
                                value={normalizeQuestionTypeForSelect(question.questionType)}
                                onChange={(e) => handleQuestionChange('questionType', e.target.value)}
                                className="bss bw1px w100"
                                style={{
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    border: errors.questionType ? '1px solid #dc3545' : '1px solid #ddd',
                                    fontSize: '0.95rem',
                                    transition: 'all 0.2s ease',
                                    outline: 'none',
                                    background: 'white',
                                    color: '#333',
                                    cursor: 'pointer'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                                onBlur={(e) => e.target.style.borderColor = errors.questionType ? '#dc3545' : '#ddd'}
                            >
                                <option value="">Select Type</option>
                                {getQuestionTypeSelectOptions(question.questionType).map(type => (
                                    <option key={type.value} value={type.value}>{type.label}</option>
                                ))}
                            </select>
                            {errors.questionType && (
                                <small className="fs11px mt0-375rem" style={{ color: '#dc3545' }}>{errors.questionType}</small>
                            )}
                        </div>

                        {/* Question */}
                        <div className="df fdc" style={{ gridColumn: '1 / -1' }}>
                            <label className="fs0-875rem fw400 mb0-625rem" style={{ color: '#555' }}>
                                Question <span style={{ color: '#dc3545' }}>*</span>
                            </label>

                            <textarea
                                value={question.qDescription || ''}
                                onChange={(e) => handleQuestionChange('qDescription', e.target.value)}
                                className="bss bw1px w100"
                                style={{
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    border: errors.qDescription ? '1px solid #dc3545' : '1px solid #ddd',
                                    fontSize: '0.95rem',
                                    transition: 'all 0.2s ease',
                                    outline: 'none',
                                    background: 'white',
                                    fontFamily: 'inherit',
                                    resize: 'vertical',
                                    minHeight: '60px'
                                }}
                                placeholder="e.g., Do you have Investor Deck documents ready for review/diligence?"
                                rows="2"
                                onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                                onBlur={(e) => e.target.style.borderColor = errors.qDescription ? '#dc3545' : '#ddd'}
                            />
                            {errors.qDescription && (
                                <small className="fs11px mt0-375rem" style={{ color: '#dc3545' }}>{errors.qDescription}</small>
                            )}
                        </div>

                        {/* Tooltip */}
                        <div className="df fdc" style={{ gridColumn: '1 / -1' }}>
                            <label className="fs0-875rem fw400 mb0-625rem" style={{ color: '#555' }}>
                                Tooltip
                            </label>
                            <textarea
                                value={tooltip}
                                onChange={(e) => handleTooltipChange(e.target.value)}
                                className="bss bw1px w100"
                                style={{
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd',
                                    fontSize: '0.875rem',
                                    transition: 'all 0.2s ease',
                                    outline: 'none',
                                    background: 'white',
                                    fontFamily: 'inherit',
                                    resize: 'vertical'
                                }}
                                placeholder="e.g., This document helps investors understand your business model"
                                rows="2"
                                onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                                onBlur={(e) => e.target.style.borderColor = '#ddd'}
                            />
                            <small className="fs11px mt0-375rem" style={{ color: '#999' }}>
                                Helpful information shown when users hover over this question
                            </small>
                        </div>

                        {/* Toolbox Offer */}
                        <div className="df fdc" style={{ gridColumn: '1 / -1' }}>
                            <label className="fs0-875rem fw400 mb0-625rem" style={{ color: '#555' }}>
                                Toolbox Offer
                            </label>
                            <textarea
                                value={toolboxOffer}
                                onChange={(e) => handleToolboxOfferChange(e.target.value)}
                                className="bss bw1px w100"
                                style={{
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd',
                                    fontSize: '0.875rem',
                                    transition: 'all 0.2s ease',
                                    outline: 'none',
                                    background: 'white',
                                    fontFamily: 'inherit',
                                    resize: 'vertical'
                                }}
                                placeholder="e.g., We can help you create an investor deck - Contact us for templates and guidance"
                                rows="2"
                                onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                                onBlur={(e) => e.target.style.borderColor = '#ddd'}
                            />
                            <small className="fs11px mt0-375rem" style={{ color: '#999' }}>
                                Offer additional resources or services related to this question
                            </small>
                        </div>

                        {/* Risk Description */}
                        <div className="df fdc" style={{ gridColumn: '1 / -1' }}>
                            <label className="fs0-875rem fw400 mb0-625rem" style={{ color: '#555' }}>
                                Risk Description
                            </label>
                            <textarea
                                value={riskDescription}
                                onChange={(e) => handleRiskDescriptionChange(e.target.value)}
                                className="bss bw1px w100"
                                style={{
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd',
                                    fontSize: '0.875rem',
                                    transition: 'all 0.2s ease',
                                    outline: 'none',
                                    background: 'white',
                                    fontFamily: 'inherit',
                                    resize: 'vertical'
                                }}
                                placeholder="e.g., Failure to address this may expose the company to significant risk"
                                rows="2"
                                onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                                onBlur={(e) => e.target.style.borderColor = '#ddd'}
                            />
                            <small className="fs11px mt0-375rem" style={{ color: '#999' }}>
                                Describe the risk associated with a negative or incomplete answer
                            </small>
                        </div>

                        {/* Recommend Description */}
                        <div className="df fdc" style={{ gridColumn: '1 / -1' }}>
                            <label className="fs0-875rem fw400 mb0-625rem" style={{ color: '#555' }}>
                                Recommend Description
                            </label>
                            <textarea
                                value={recommendDescription}
                                onChange={(e) => handleRecommendDescriptionChange(e.target.value)}
                                className="bss bw1px w100"
                                style={{
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd',
                                    fontSize: '0.875rem',
                                    transition: 'all 0.2s ease',
                                    outline: 'none',
                                    background: 'white',
                                    fontFamily: 'inherit',
                                    resize: 'vertical'
                                }}
                                placeholder="e.g., We recommend preparing a comprehensive investor deck with key metrics"
                                rows="2"
                                onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                                onBlur={(e) => e.target.style.borderColor = '#ddd'}
                            />
                            <small className="fs11px mt0-375rem" style={{ color: '#999' }}>
                                Provide a recommendation or suggested action for this question
                            </small>
                        </div>

                        {/* Predefined Options (yesNo, YN/F/T, D/ND, 1to5) */}
                        {hasPredefinedOptions() && (
                            <div className="df fdc" style={{ gridColumn: '1 / -1', marginTop: '0.5rem' }}>
                                <div className="df aic mb1rem" style={{ gap: '8px' }}>
                                    <label className="fs0-875rem fw400" style={{ color: '#555' }}>
                                        Answer Options <span style={{ color: '#dc3545' }}>*</span>
                                    </label>
                                    <InfoOutlined style={{ fontSize: '16px', color: '#999' }} />
                                    <small style={{ color: '#999', fontSize: '0.8rem' }}>
                                        Add info/description for each option
                                    </small>
                                </div>
                                <div className="df fdc" style={{ gap: '12px' }}>
                                    {PREDEFINED_OPTIONS[effectiveQuestionType].map((option) => (
                                        <div key={option} className="br8px p1rem" style={{
                                            background: 'white',
                                            border: '1px solid #e0e0e0'
                                        }}>
                                            <div className="df aic mb0-5rem">
                                                <div className="df aic jcc" style={{
                                                    background: '#4CAF50',
                                                    color: 'white',
                                                    padding: '6px 12px',
                                                    borderRadius: '6px',
                                                    fontSize: '0.875rem',
                                                    fontWeight: '500',
                                                    minWidth: '80px'
                                                }}>
                                                    {option}
                                                </div>
                                            </div>
                                            <div className="df fdc">
                                                <textarea
                                                    value={predefinedOptionsInfo[option] || ''}
                                                    onChange={(e) => handlePredefinedOptionInfo(option, e.target.value)}
                                                    className="bss bw1px w100"
                                                    style={{
                                                        padding: '8px 10px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #ddd',
                                                        fontSize: '0.875rem',
                                                        outline: 'none',
                                                        background: 'white',
                                                        fontFamily: 'inherit',
                                                        resize: 'vertical'
                                                    }}
                                                    placeholder={`e.g., This option indicates ${option.toLowerCase()}`}
                                                    rows="2"
                                                    onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                                                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Date Type Info Field */}
                        {isDateType() && (
                            <div className="df fdc" style={{ gridColumn: '1 / -1', marginTop: '0.5rem' }}>
                                <div className="df aic mb0-75rem" style={{ gap: '8px' }}>
                                    <label className="fs0-875rem fw400" style={{ color: '#555' }}>
                                        Date Field Guidelines
                                    </label>
                                    <InfoOutlined style={{ fontSize: '16px', color: '#999' }} />
                                    <small style={{ color: '#999', fontSize: '0.8rem' }}>
                                        Add instructions for the date field
                                    </small>
                                </div>
                                <textarea
                                    value={dateInfo}
                                    onChange={(e) => handleDateInfoChange(e.target.value)}
                                    className="bss bw1px w100"
                                    style={{
                                        padding: '10px 12px',
                                        borderRadius: '8px',
                                        border: '1px solid #ddd',
                                        fontSize: '0.95rem',
                                        outline: 'none',
                                        background: 'white',
                                        fontFamily: 'inherit',
                                        resize: 'vertical'
                                    }}
                                    placeholder="e.g., Please select the expected completion date in DD/MM/YYYY format."
                                    rows="3"
                                    onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                                />
                            </div>
                        )}

                        {/* Custom Options (DropDown, Radio, Multi) */}
                        {needsCustomOptions() && (
                            <div className="df fdc" style={{ gridColumn: '1 / -1', marginTop: '0.5rem' }}>
                                <div className="df aic mb1rem" style={{ gap: '8px' }}>
                                    <label className="fs0-875rem fw400" style={{ color: '#555' }}>
                                        {effectiveQuestionType} Options <span style={{ color: '#dc3545' }}>*</span>
                                    </label>
                                    <InfoOutlined style={{ fontSize: '16px', color: '#999' }} />
                                    <small style={{ color: '#999', fontSize: '0.8rem' }}>
                                        Add value and info for each option
                                    </small>
                                </div>
                                <div className="df fdc" style={{ gap: '12px' }}>
                                    {customOptions.map((option, optIdx) => (
                                        <div key={optIdx} className="br8px p1rem" style={{
                                            background: 'white',
                                            border: '1px solid #e0e0e0'
                                        }}>
                                            <div className="df aic jcsb mb0-75rem">
                                                <span className="fs0-85rem fw500" style={{ color: '#333' }}>
                                                    Option {optIdx + 1}
                                                </span>
                                                {customOptions.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeCustomOption(optIdx)}
                                                        className="df aic jcc cp"
                                                        style={{
                                                            border: 'none',
                                                            background: 'transparent',
                                                            color: '#dc3545',
                                                            padding: '4px',
                                                            borderRadius: '6px',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onMouseOver={(e) => {
                                                            e.currentTarget.style.background = '#fee';
                                                            e.currentTarget.style.color = '#c82333';
                                                        }}
                                                        onMouseOut={(e) => {
                                                            e.currentTarget.style.background = 'transparent';
                                                            e.currentTarget.style.color = '#dc3545';
                                                        }}
                                                        title="Remove Option"
                                                    >
                                                        <Delete style={{ fontSize: '16px' }} />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="dg gg0-75rem mb0-75rem">
                                                <div className="df fdc">
                                                    <label className="fs0-8rem fw400 mb0-5rem" style={{ color: '#666' }}>
                                                        Option Value <span style={{ color: '#dc3545' }}>*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={customOptionPrimaryLabel(option)}
                                                        onChange={(e) => handleCustomOptionPrimaryChange(optIdx, e.target.value)}
                                                        className="bss bw1px w100 mb0-5rem"
                                                        style={{
                                                            padding: '8px 10px',
                                                            borderRadius: '6px',
                                                            border: '1px solid #ddd',
                                                            fontSize: '0.875rem',
                                                            outline: 'none',
                                                            background: 'white'
                                                        }}
                                                        placeholder={`e.g., Option ${optIdx + 1}`}
                                                        onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                                                        onBlur={(e) => e.target.style.borderColor = '#ddd'}
                                                    />
                                                </div>
                                            </div>
                                            <div className="df fdc">
                                                <textarea
                                                    value={customOptionSecondaryText(option)}
                                                    onChange={(e) => handleCustomOptionSecondaryChange(optIdx, e.target.value)}
                                                    className="bss bw1px w100"
                                                    style={{
                                                        padding: '8px 10px',
                                                        borderRadius: '6px',
                                                        border: '1px solid #ddd',
                                                        fontSize: '0.875rem',
                                                        outline: 'none',
                                                        background: 'white',
                                                        fontFamily: 'inherit',
                                                        resize: 'vertical'
                                                    }}
                                                    placeholder="e.g., Select this option if..."
                                                    rows="2"
                                                    onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                                                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={addCustomOption}
                                        className="df aic jcc cp"
                                        style={{
                                            border: 'none',
                                            background: '#4CAF50',
                                            color: 'white',
                                            padding: '10px 16px',
                                            borderRadius: '8px',
                                            fontSize: '0.875rem',
                                            fontWeight: '500',
                                            gap: '6px',
                                            transition: 'all 0.2s ease',
                                            alignSelf: 'flex-start'
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.background = '#45a049'}
                                        onMouseOut={(e) => e.currentTarget.style.background = '#4CAF50'}
                                    >
                                        <Add style={{ fontSize: '18px' }} />
                                        Add Option
                                    </button>
                                </div>
                                {errors.customOptions && (
                                    <small className="fs11px mt0-75rem" style={{ color: '#dc3545', display: 'block' }}>
                                        {errors.customOptions}
                                    </small>
                                )}
                            </div>
                        )}

                        {/* Risk Score Slider - Show for questions with options */}
                        {question.questionType && (
                            <div style={{ gridColumn: '1 / -1' }}>
                                <RiskScoreSlider
                                    questionType={effectiveQuestionType}
                                    customOptions={customOptions}
                                    predefinedOptionsInfo={predefinedOptionsInfo}
                                    riskScores={riskScores}
                                    onRiskScoreChange={handleRiskScoreChange}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuestionForm;
